import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Key, CheckCircle, XCircle, AlertCircle, RefreshCw, Send, Power, PowerOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const AdminEmailSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('onboarding@resend.dev');
  const [error, setError] = useState(null);

  // Get session token from cookie or localStorage
  const getSessionToken = () => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') return value;
    }
    return localStorage.getItem('session_token');
  };

  const fetchSettings = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSettings(response.data);
      if (response.data.from_email) {
        setFromEmail(response.data.from_email);
      }
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError({ type: 'forbidden', message: 'Admin access required. You do not have permission to view this page.' });
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError({ type: 'error', message: 'Failed to load email settings' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    if (!apiKey.startsWith('re_')) {
      toast.error('Invalid API key format. Resend keys start with "re_"');
      return;
    }

    setSaving(true);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/admin/email-settings`, 
        { api_key: apiKey, from_email: fromEmail },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success('Email settings saved successfully');
      setApiKey(''); // Clear input after save
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/admin/email-settings/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Test email sent to ${response.data.recipient}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async (enable) => {
    try {
      const token = getSessionToken();
      const endpoint = enable ? 'enable' : '';
      const method = enable ? 'post' : 'delete';
      
      await axios({
        method,
        url: enable ? `${API}/admin/email-settings/enable` : `${API}/admin/email-settings`,
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success(enable ? 'Email sending enabled' : 'Email sending disabled');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error?.type === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Access Denied</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">Email Settings</h1>
          <p className="text-muted-foreground mt-2">Configure transactional email for password resets</p>
        </div>

        {/* Status Card */}
        <Card data-testid="email-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Current Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {settings?.configured ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      {settings?.configured ? 'API Key Configured' : 'API Key Not Configured'}
                    </p>
                    {settings?.from_email && (
                      <p className="text-sm text-muted-foreground">From: {settings.from_email}</p>
                    )}
                  </div>
                </div>
                {settings?.configured && (
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    settings.email_enabled 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {settings.email_enabled ? 'Enabled' : 'Disabled'}
                  </div>
                )}
              </div>

              {settings?.last_updated && (
                <p className="text-xs text-muted-foreground">
                  Last updated: {new Date(settings.last_updated).toLocaleString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration Card */}
        <Card data-testid="email-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {settings?.configured ? 'Update Configuration' : 'Initial Setup'}
            </CardTitle>
            <CardDescription>
              {settings?.configured 
                ? 'Enter a new API key to rotate credentials'
                : 'Enter your Resend API key to enable password reset emails'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="apiKey">Resend API Key</Label>
              <Input
                id="apiKey"
                type="password"
                placeholder="re_..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="font-mono"
                data-testid="resend-api-key-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get your key at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com</a>
              </p>
            </div>

            <div>
              <Label htmlFor="fromEmail">From Email</Label>
              <Input
                id="fromEmail"
                type="email"
                placeholder="onboarding@resend.dev"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                data-testid="from-email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use onboarding@resend.dev for testing, or verify your domain at Resend
              </p>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={!apiKey || saving}
              className="w-full"
              data-testid="save-email-settings-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Key className="mr-2 h-4 w-4" />
                  {settings?.configured ? 'Rotate Key' : 'Save Configuration'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Actions Card (only if configured) */}
        {settings?.configured && (
          <Card data-testid="email-actions-card">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={handleTest} 
                disabled={testing || !settings.email_enabled}
                variant="outline"
                className="w-full"
                data-testid="test-email-btn"
              >
                {testing ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Test Email
                  </>
                )}
              </Button>

              {settings.email_enabled ? (
                <Button 
                  onClick={() => handleToggle(false)} 
                  variant="destructive"
                  className="w-full"
                  data-testid="disable-email-btn"
                >
                  <PowerOff className="mr-2 h-4 w-4" />
                  Disable Email Sending
                </Button>
              ) : (
                <Button 
                  onClick={() => handleToggle(true)} 
                  variant="default"
                  className="w-full"
                  data-testid="enable-email-btn"
                >
                  <Power className="mr-2 h-4 w-4" />
                  Enable Email Sending
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Back to Dashboard */}
        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailSettings;
